import "./env"; // Validates the env at module load; throws on a missing or invalid value.

import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { configureAuthNetwork } from "@linkhr/auth";
import { createAppRoot } from "@linkhr/auth/react";
import { AuthSplash } from "@linkhr/auth/pages";
import { initI18n } from "@linkhr/i18n";
import { ErrorScreen, NotFoundScreen } from "@linkhr/design-system";
import { reportCompanyAccessLost } from "./shell/companyAccess";
import { routeTree } from "./routeTree.gen";
import { env } from "./env";
import "./styles/app.css";

/**
 * Link Accounting — signs in with the `accounting` audience and talks to the
 * real backend. The design preview under `/prototype` runs on fixture data.
 *
 * Its own query client: a company the person loses mid-session
 * (`COMPANY_ACCESS_REVOKED` on any call) is reported to the signed-in shell.
 */
function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({ onError: reportCompanyAccessLost }),
    mutationCache: new MutationCache({ onError: (error) => reportCompanyAccessLost(error) }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        // A refusal (4xx) is an answer; only a server error is worth asking again.
        retry: (failureCount, error) => {
          const status = (error as { status?: unknown }).status;
          return typeof status === "number" ? status >= 500 && failureCount < 2 : failureCount < 1;
        },
      },
      mutations: { retry: false },
    },
  });
}

async function boot(): Promise<void> {
  // createAppRoot repeats this; it runs first so anything read before it
  // (the session's tenant slug) is this audience's.
  configureAuthNetwork({ baseUrl: env.VITE_API_BASE_URL, aud: "accounting" });
  await initI18n({
    backendLoadPath: `${import.meta.env.BASE_URL}locales/{{lng}}/{{ns}}.json`,
  });
  await createAppRoot({
    aud: "accounting",
    routeTree,
    apiBaseUrl: env.VITE_API_BASE_URL,
    SplashComponent: AuthSplash,
    queryClient: createQueryClient(),
    DefaultNotFoundComponent: () => <NotFoundScreen homeHref={import.meta.env.BASE_URL} />,
    DefaultErrorComponent: ErrorScreen,
  });
}

void boot();
